import DeleteButton from '../components/DeleteButton';
import DeleteIconButton from '../components/DeleteIconButton';
import { Box, FormControl, Grid, InputAdornment, InputLabel, Link, Select, TextField } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { useSnackbar } from 'notistack';
import React from 'react';
import { useDropzone } from 'react-dropzone';
import { useHistory } from 'react-router-dom';
import AccessTagsDropdown from '../components/AccessTagsDropdown';
import InfoTip from '../components/InfoTip';
import LicenseeFormField from '../components/LicenseeFormField';
import RemoteAutocomplete from '../components/RemoteAutocomplete';
import SystemTagsDropdown from '../components/SystemTagsDropdown';
import TimezonePicker from '../components/TimezonePicker';
import UploadImageButton from '../components/UploadImageButton';
import config from '../config';
import { ContextStore } from '../store/contextStore';
import { tripHasDepartureCategory } from '../utils/tagCategories';
import validURL from '../utils/validators/url';
import UploadKMLFileButton from './TripBuilder/UploadKMLFileButton';
import TripEditInfoBelowFirstBox from './TripEditInfoBelowFirstBox';
import { positiveFeedbackTargetsOptions, TARGET_OTA_OR_URL, TARGET_URL, TRIP_TYPE_BUS, TRIP_TYPE_TOUR } from './utils';

// const DEFAULT_EDITOR_OPTIONS = {
//     height: 300,
//     width: '100%',
//     max_width: '100%',
//     menubar: false,
//     plugins: ['advlist autolink autoresize lists link image charmap print preview anchor', 'searchreplace visualblocks code fullscreen', 'insertdatetime media table paste code help wordcount'],
//     toolbar: 'undo redo | code | link | formatselect | bold italic backcolor | alignleft aligncenter alignright alignjustify | bullist numlist outdent indent | removeformat | help',
//     content_style: 'body { font-family:Helvetica,Arial,sans-serif; font-size:14px }',
//     skin: 'material-classic',
//     force_br_newlines: false,
//     force_p_newlines: false,
//     forced_root_block: '',
// };

const useStyles = makeStyles((theme) => ({
    root: {
        backgroundColor: ' #FCFCFE',
        [theme.breakpoints.down('sm')]: {
            paddingBottom: '8rem !important',
        },
    },
    styledBox: {
        padding: '2rem',
        backgroundColor: 'white',
        height: '100%',
        borderRadius: '12px',
        margin: '0rem 1rem 1rem 1rem',
        [theme.breakpoints.down('sm')]: {
            padding: '1rem',
            margin: '0rem',
            overflowY: 'auto',
        },
        [theme.breakpoints.up('sm')]: {
            border: '1px solid #D8D8D8',
        },
    },
    uploadLabel: {
        position: 'absolute',
        right: theme.spacing(2),
        bottom: theme.spacing(2),
        // transform: "translate(-50%, -50%)",
        textTransform: 'uppercase',
        whiteSpace: 'nowrap',
        fontWeight: 'bold',
        color: '#ffffff',
        display: 'flex',
        alignItems: 'center',
    },
    fieldsColumn: {
        maxWidth: 691,
        width: '100%',
    },
    compactControlLabel: {
        '& .MuiFormControlLabel-label': {
            lineHeight: 1.4,
        },
        '& .MuiCheckbox-root, & .MuiRadio-root': {
            paddingTop: 4,
            paddingBottom: 4,
        },
    },
}));

// const emptyBookingRef = {
//     created_by: null,
//     created_ts: moment().format(),
//     first_name: "",
//     group_tour: null,
//     id: null,
//     last_name: "",
//     max_people: 0,
//     number: "",
//     provider: {},
//     registrations_count: 0,
// }


const compare = (a, b) => {
    if (a.idx < b.idx) {
        return -1;
    }
    if (a.idx > b.idx) {
        return 1;
    }
    return 0;
};

const checkAndChooseExistingLink = (trip) => {
    let foundLink = trip.sr_links.filter((link) => link.date === null);
    if (foundLink && foundLink[0]?.link) {
        return foundLink[0].link;
    } else {
        return null;
    }
};

const TripEditInfo = (props) => {
    const { hasPerm, tourOperator } = React.useContext(ContextStore);
    const classes = useStyles();
    const { data: trip, onChange: persistTrip, clientErrors = {}, setClientErrors, isSmallScreen, syncActive, onDeleteTripList, handleOnBlur } = props;
    const tripRef = React.useRef(trip);
    tripRef.current = trip;
    const history = useHistory();

    // editorRef kept for future editors; currently unused
    // const editorRef = React.useRef(null);

    let existingLink = checkAndChooseExistingLink(trip);
    const [srLink, setSrLink] = React.useState(existingLink ? existingLink : null);
    // const existingDepartureIds = (trip.departures || []).map((x) => x.tag.id);
    const { enqueueSnackbar } = useSnackbar();

    const isExistingImage = trip.cover_image && trip.cover_image.p && trip.cover_image.s;

    // TODO: remove this!
    // const hasWritePerm = hasPerm('tour', 'write')
    const hasWritePerm = true;

    const tripType = trip.trip_type ?? TRIP_TYPE_TOUR;

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const onDrop = React.useCallback((acceptedFiles) => {
        onChange({
            ...tripRef.current,
            cover_image: acceptedFiles[0],
            _upload: true,
        });
        // props.onImageSelect && props.onImageSelect(acceptedFiles[0], props.idx);
        // props.onUpload && props.onUpload(getUpdate(), props.idx, acceptedFiles[0]);
    });

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const onMeetingPointPhotoDrop = React.useCallback((acceptedFiles) => {
        const t = tripRef.current;
        onChange({
            ...t,
            meeting_point_photo: acceptedFiles[0],
            meeting_point: {
                ...(t.meeting_point || {}),
                photo: acceptedFiles[0],
            },
            _upload: true,
        });
    });

    const onKMLFileSelectTour = (file) => {
        const t = tripRef.current;
        onChange({
            ...t,
            itinerary: {
                ...(t.itinerary || {}),
                kml_file: file,
            },
            kml_file: file,
            _upload: !!file,
        });
    };

    const onKMLFileSelectBus = (file) => {
        const t = tripRef.current;
        const vehicleRouteSet = t.vehicle_route_set;
        const nextVehicleRouteSet = vehicleRouteSet && typeof vehicleRouteSet === 'object'
            ? { ...vehicleRouteSet, kml_file: file }
            : (vehicleRouteSet ? { id: vehicleRouteSet, kml_file: file } : { kml_file: file });

        onChange({
            ...t,
            vehicle_route_set: nextVehicleRouteSet,
            kml_file: file,
            _upload: !!file,
        });
    };
    const dz = useDropzone({
        noClick: true,
        noKeyboard: true,
        maxFiles: 1,
        multiple: false,
        onDrop: onDrop,
    });
    const meetingPointPhotoDz = useDropzone({
        noClick: true,
        noKeyboard: true,
        maxFiles: 1,
        multiple: false,
        onDrop: onMeetingPointPhotoDrop,
    });

    // const handleTabChange = (ev, newTab) => {
    //     const match = Object.keys(paramsToTab).filter((x) => paramsToTab[x] === newTab);
    //     if (match && match.length === 1) {
    //         history.push(`/trip/${urlParams.id}/${urlParams.tab}/${match[0]}`);
    //     }
    // };

    const onChange = (update) => {
        if (persistTrip) persistTrip(update);
    };

    const onLicenseeChange = (id) => {
        onChange({
            ...tripRef.current,
            tour_operator: id,
            _changed: true,
        });
    };

    const onCrosssellsChange = (ev, crosssells) => {
        props.onCrosssellsChange(crosssells);
    };

    const onTimezoneChange = (ev, nv) => {
        onChange({
            ...tripRef.current,
            start_timezone: nv?.value,
        });
    };

    const onLaunchPolicyChange = (ev, nv) => {
        onChange({
            ...tripRef.current,
            launch_policy: nv,
        });
    };

    const onSwitchChange = (ev) => {
        onChange({
            ...tripRef.current,
            [ev.target.name]: ev.target.checked,
        });
    };

    const onTextChange = (ev) => {
        onChange({
            ...tripRef.current,
            [ev.target.name]: ev.target.value,
            _changed: true,
        });
    };

    const onSelectChange = (ev) => {
        props.onChange(
            {
                ...props.data,
                [ev.target.name]: ev.target.value,
            },
            props.idx
        );
    };

    const onContextTextChange = (ev) => {
        const t = tripRef.current;
        onChange({
            ...t,
            context: {
                ...(t.context || {}),
                [ev.target.name]: ev.target.value,
            },
            _changed: true,
        });
    };

    const onMeetingPointChange = (ev) => {
        const t = tripRef.current;
        const mp = { ...(t.meeting_point || {}) };
        mp[ev.target.name] = ev.target.value;
        onChange({
            ...t,
            meeting_point: mp,
            _changed: true,
        });
    };

    const handleLocationRadioChange = (e) => {
        onChange({
            ...tripRef.current,
            location_sharing: parseInt(e.target.value),
        });
    };

    // Self registration links
    React.useEffect(() => {
        let token = localStorage.bootstrap;
        token = JSON.parse(token);
        const createNewLink = async () => {
            let res = await fetch(`${config.apiUrl}/selfreg_link/`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', Authorization: `Token ${token.token}` },
                body: JSON.stringify({ group_tour: trip.id }),
            });
            res = await res.json();
            setSrLink(res.link);
        };

        if (trip.sr_links.length < 1 || !srLink) {
            createNewLink();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const onAdditionalExternalIDAdd = () => {
        const new_id = window.prompt("Please enter the new external ID");
        if (new_id === null) {
            return;
        }
        if (!new_id || new_id.trim().length <= 0) {
            return enqueueSnackbar('External ID must not be empty', { variant: 'error' });
        }
        const t = tripRef.current;
        let sorted_additional_custom_ids = [
            ...t.additional_custom_ids,
            {
                idx: t.additional_custom_ids.length,
                custom_id: new_id,
                id_type: 'product_id',
                use_for_matching: true,
                api_queryable: true,
                integration: null,
                restech_account: null,
                // The server creates every editor-made row as operator-owned, whatever is sent.
                managed: false,
            },
        ];
        let aci_idx_counter = -1;
        sorted_additional_custom_ids = sorted_additional_custom_ids.map((aci) => {
            aci_idx_counter = aci_idx_counter + 1;
            return { ...aci, idx: aci_idx_counter };
        });
        onChange({
            ...t,
            additional_custom_ids: sorted_additional_custom_ids.sort(compare),
            _changed: true,
        });
    }

    const onAdditionalExternalIDChange = (aeid, idx) => {
        // let custom_id_to_update = trip.additional_custom_ids.filter((aci) => aci.idx === idx)[0];
        // custom_id_to_update.custom_id = ev.target.value;
        // trip.additional_custom_ids = [...trip.additional_custom_ids.filter((aci) => aci.idx !== idx), custom_id_to_update];
        const t = tripRef.current;
        onChange({
            ...t,
            additional_custom_ids: (t.additional_custom_ids || []).map((x, i) => (i === +idx ? aeid : x)),
            _changed: true,
        });
    };

    const handleRemoveAdditionalCustomId = (idx) => {
        const t = tripRef.current;
        let sorted_additional_custom_ids = [...t.additional_custom_ids.filter((aci) => aci.idx !== idx)];
        let aci_idx_counter = -1;
        sorted_additional_custom_ids = sorted_additional_custom_ids.map((aci) => {
            aci_idx_counter = aci_idx_counter + 1;
            return { ...aci, idx: aci_idx_counter };
        });
        const newTrip = {
            ...t,
            additional_custom_ids: sorted_additional_custom_ids.sort(compare),
            _changed: true,
        };
        props.checkAdditionalCustomIds(newTrip);
        onChange(newTrip);
    };

    const handleReviewChange = (ev) => {
        const t = tripRef.current;
        if (ev.target.name === 'review_name' || ev.target.name === 'review_link') {
            if (ev.target.name.includes('name')) {
                onChange({
                    ...t,
                    review: { ...(t.review || {}), name: ev.target.value },
                    _changed: true,
                });
            } else {
                onChange({
                    ...t,
                    review: { ...(t.review || {}), url: ev.target.value },
                    _changed: true,
                });
            }
        } else {
            if (ev.target.name.includes('name')) {
                onChange({
                    ...t,
                    photo_sharing: { ...(t.photo_sharing || {}), name: ev.target.value },
                    _changed: true,
                });
            } else {
                onChange({
                    ...t,
                    photo_sharing: { ...(t.photo_sharing || {}), url: ev.target.value },
                    _changed: true,
                });
            }
        }
    };

    const checkMeetingPointName = () => {
        const t = tripRef.current;
        props.setClientErrors(prev => ({
            ...prev,
            missingMeetingPointName: !!t.meeting_point?.maps_url && !t.meeting_point?.name
        }));
    };

    const checkValidURL = () => {
        const t = tripRef.current;
        props.setClientErrors((prev) => {
            let errors = { ...prev };
            errors.invalidURLReview = false;
            errors.invalidURLPhotoSharing = false;
            errors.invalidURLMeetingPointURL = false;
            errors.local_recommendations_link = false;
            errors.cancellation_policy_link = false;
            errors.notes_link = false;
            errors.waiver_link = false;
            errors.positive_review_url = false;
            errors.upsell_link = false;
            errors.missingPositiveReviewUrl = false;
            errors.missingMeetingPointName = false;
            if (!validURL(t.review?.url)) {
                errors.invalidURLReview = true;
            }
            if (!validURL(t.photo_sharing?.url)) {
                errors.invalidURLPhotoSharing = true;
            }
            if (!validURL(t.context?.upsell_link)) {
                errors.upsell_link = true;
            }

            if (t.meeting_point?.maps_url) {
                errors.invalidURLMeetingPointURL = !validURL(t.meeting_point?.maps_url);
                errors.missingMeetingPointName = !t.meeting_point?.name;
            }

            if (t.context?.local_recommendations_link && !validURL(t.context?.local_recommendations_link)) {
                errors.local_recommendations_link = true;
            }

            if (t.context?.cancellation_policy_link && !validURL(t.context?.cancellation_policy_link)) {
                errors.cancellation_policy_link = true;
            }

            if (t.context?.notes_link && !validURL(t.context?.notes_link)) {
                errors.notes_link = true;
            }

            if (t.context?.waiver_link && !validURL(t.context?.waiver_link)) {
                errors.waiver_link = true;
            }

            if ([TARGET_URL].indexOf(+t.positive_review_target) >= 0) {
                errors.missingPositiveReviewUrl = !t.positive_review_url;
            }

            if ([TARGET_OTA_OR_URL, TARGET_URL].indexOf(+t.positive_review_target) >= 0) {
                errors.positive_review_url = !validURL(t.positive_review_url);
            }

            return { ...errors };
        });
    };

    const onUseOperatorDefaultsToggle = React.useCallback((e) => {
        if (!persistTrip) {
            return;
        }
        persistTrip({
            ...tripRef.current,
            use_operator_defaults: e.target.checked,
            _changed: true,
        });
    }, [persistTrip]);

    //Associated tag categories management

    const onAccessTagsChange = (ev, atags) => {
        onChange((prevState) => ({ ...prevState, atag: atags }));
    };

    const onSystemTagsChange = (ev, stags) => {
        onChange((prevState) => ({ ...prevState, tags: stags }));
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
    React.useEffect(() => {
        // Counts the operator defaults the "Use Tour Operator default settings" switch brings in (CP-7922).
        const hasDepartureTagCategory = tripHasDepartureCategory(trip, tourOperator);
        if (trip.has_departures !== hasDepartureTagCategory) {
            onChange((prevState) => ({
                ...prevState,
                has_departures: hasDepartureTagCategory,
                departures: hasDepartureTagCategory ? prevState.departures : [],
            }));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [trip.tag_categories, trip.use_operator_defaults, trip.has_departures, tourOperator]);

    return (
        <div className={classes.root}>
            <Box className={classes.styledBox} style={{ borderRadius: '0 0 12px 12px' }}>
                <Grid container direction="row" spacing={2} alignItems="flex-start">
                    <Grid item xs={12} md={6} style={{ order: 2 }}>
                        <input {...dz.getInputProps()} />
                        {/* CP-5556: the trash sits on the image's top-right corner, as everywhere else. */}
                        <div style={{ position: 'relative' }}>
                            <UploadImageButton
                                rootStyle={{ width: '100%', height: isSmallScreen ? '40vh' : '55vh', minHeight: '300px', overflow: 'hidden' }}
                                imageStyle={{ width: '100%', height: '100%' }}
                                height={'100%'}
                                width={'100%'}
                                imageAction="overlay"
                                borderRadius={10}
                                onClick={dz.open}
                                isUpload={!isExistingImage}
                                image={trip.cover_image}
                                customPlaceHolderImage={true}
                                disabled={!hasWritePerm}
                            />
                            {trip.cover_image && (
                                <span style={{ position: 'absolute', top: 11, right: 11, zIndex: 10 }}>
                                    <DeleteIconButton title="Delete image" overImage allowed={hasWritePerm} onClick={() => props.handleTripCoverImageDelete()} />
                                </span>
                            )}
                        </div>
                    </Grid>
                    <Grid item xs={12} md={6} container direction="row" spacing={2} className={classes.fieldsColumn} style={{ order: 1 }}>
                        <Grid item xs={12}>
                            <LicenseeFormField value={trip.tour_operator} onChange={onLicenseeChange} disabled={!hasWritePerm} />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                disabled={!hasWritePerm}
                                variant="filled"
                                size="small"
                                label="Trip Name"
                                name="name"
                                value={trip.name || ''}
                                fullWidth
                                onChange={onTextChange}
                                required
                                onBlur={handleOnBlur}
                                error={clientErrors.missingTripName}
                                helperText={clientErrors.missingTripName && 'Trip name is required'}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            {/* CP-8309: staff-only alias. Sent as part of the whole-trip PUT; the
                                backend stores a blank as NULL. */}
                            <TextField
                                disabled={!hasWritePerm}
                                variant="filled"
                                size="small"
                                label="Internal Name (optional)"
                                name="internal_name"
                                value={trip.internal_name || ''}
                                fullWidth
                                onChange={onTextChange}
                                inputProps={{ maxLength: 1024 }}
                                InputProps={{
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <InfoTip
                                                placement="top-end"
                                                title="Only your team sees this name; guests never do. Search by it in Chat and in this Trips list, alongside the Trip Name."
                                                sx={{ fontSize: 18, color: 'text.secondary' }}
                                            />
                                        </InputAdornment>
                                    ),
                                }}
                            />
                        </Grid>
                        {/* CP-8349: dates and time zones mean nothing to a guide group (TripCreate
                            hides the same control), so don't ask for one on edit either. */}
                        {!trip.is_staff_chat_group && (
                            <Grid item xs={12}>
                                <TimezonePicker label="Starting Timezone" disabled={!hasWritePerm} required variant="filled" value={trip.start_timezone || ''} onChange={onTimezoneChange} showTimezoneOffset={true} helperText="Time zone in which the trip starts" />
                            </Grid>
                        )}
                        <Grid item xs={12}>
                            <RemoteAutocomplete
                                label="Launch Policy"
                                value={trip.launch_policy || undefined}
                                onChange={onLaunchPolicyChange}
                                disabled={!hasWritePerm}
                                InputProps={{ required: true }}
                                textFieldProps={{
                                    placeholder: 'Default',
                                    helperText: 'If left empty, tour operator default is used',
                                }}
                                resource="launch_policy"
                                urlSuffix={null}
                                variant="filled"
                                size="small"
                                getOptionLabel={(opt) => {
                                    return opt.name;
                                }}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <AccessTagsDropdown value={trip.atag} disabled={!hasWritePerm} onChange={onAccessTagsChange} />
                        </Grid>
                        <Grid item xs={12}>
                            <SystemTagsDropdown value={trip.tags} disabled={!hasWritePerm} onChange={onSystemTagsChange} />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField inputProps={{ maxLength: 80 }} disabled={!hasWritePerm} helperText={`${80 - trip.description?.length} characters left`} variant="filled" size="small" label="Description (optional)" name="description" value={trip.description || ''} fullWidth multiline onChange={onTextChange} />
                        </Grid>
                        {tripType === TRIP_TYPE_TOUR && trip.itinerary && (
                            <Grid item xs={12}>
                                <UploadKMLFileButton
                                    file={trip.itinerary.kml_file}
                                    disabled={!hasWritePerm}
                                    onSelect={!hasPerm('tour', 'write') ? () => enqueueSnackbar('You do not have permission to edit trips', { variant: 'error' }) : onKMLFileSelectTour}
                                    accept=".kml, *.kmz"
                                />
                            </Grid>
                        )}
                        {tripType === TRIP_TYPE_BUS && trip.vehicle_route_set && (
                            <Grid item xs={12}>
                                <UploadKMLFileButton
                                    file={trip.vehicle_route_set.kml_file}
                                    disabled={!hasWritePerm}
                                    onSelect={!hasPerm('tour', 'write') ? () => enqueueSnackbar('You do not have permission to edit trips', { variant: 'error' }) : onKMLFileSelectBus}
                                    accept=".kml, *.kmz"
                                />
                            </Grid>
                        )}

                        <Grid item xs={12}>
                            <FormControl variant="filled" size="small" fullWidth disabled={!hasWritePerm}>
                                <InputLabel>Positive feedback target</InputLabel>
                                <Select native value={trip.positive_review_target ?? ''} name="positive_review_target"
                                    onChange={(ev) => {
                                        onSelectChange(ev);
                                        clientErrors.positive_review_url = false;
                                        clientErrors.missingPositiveReviewUrl = false;
                                    }}>
                                    {positiveFeedbackTargetsOptions}
                                </Select>
                            </FormControl>
                        </Grid>
                        {[TARGET_OTA_OR_URL, TARGET_URL].indexOf(+trip.positive_review_target) >= 0 && (
                            <Grid item xs={12}>
                                <TextField
                                    variant="filled"
                                    size="small"
                                    label="Positive review URL"
                                    name="positive_review_url"
                                    disabled={!hasWritePerm}
                                    value={trip.positive_review_url || ''}
                                    fullWidth
                                    required
                                    onChange={onTextChange}
                                    onBlur={checkValidURL}
                                    error={clientErrors.missingPositiveReviewUrl || clientErrors.positive_review_url}
                                    helperText={(clientErrors.positive_review_url && 'Please use a valid URL') || (clientErrors.missingPositiveReviewUrl && 'Positive Review URL is required')}
                                />
                            </Grid>
                        )}
                        {trip.is_bespoke && (
                            <Grid item xs={12} style={{ marginBottom: '1rem' }}>
                                <Link href={trip.map_link} target="_blank">
                                    Open User Map
                                </Link>
                            </Grid>
                        )}
                        <Grid item xs={12} style={{ marginBottom: '1rem' }} container direction="row" spacing={1} alignItems="center">
                            <DeleteButton perm="tour.delete" disabled={syncActive} onClick={onDeleteTripList}>
                                Delete
                            </DeleteButton>
                        </Grid>
                    </Grid>
                </Grid>
            </Box>

            <TripEditInfoBelowFirstBox
                trip={trip}
                classes={classes}
                hasWritePerm={hasWritePerm}
                hasPerm={hasPerm}
                history={history}
                onTourChange={persistTrip}
                clientErrors={clientErrors}
                clientErrorsFromParent={props.clientErrors}
                setClientErrors={setClientErrors}
                isSmallScreen={isSmallScreen}
                syncActive={syncActive}
                srLink={srLink}
                isTouched={props.isTouched}
                onDeleteTripList={onDeleteTripList}
                meetingPointPhotoDz={meetingPointPhotoDz}
                handleLocationRadioChange={handleLocationRadioChange}
                onSwitchChange={onSwitchChange}
                onUseOperatorDefaultsToggle={onUseOperatorDefaultsToggle}
                onTextChange={onTextChange}
                onMeetingPointChange={onMeetingPointChange}
                checkMeetingPointName={checkMeetingPointName}
                checkValidURL={checkValidURL}
                handleOnBlur={handleOnBlur}
                handleMeetingPointPhotoDelete={props.handleMeetingPointPhotoDelete}
                onAdditionalExternalIDChange={onAdditionalExternalIDChange}
                onAdditionalExternalIDAdd={onAdditionalExternalIDAdd}
                handleRemoveAdditionalCustomId={handleRemoveAdditionalCustomId}
                handleReviewChange={handleReviewChange}
                onCrosssellsChange={onCrosssellsChange}
                onContextTextChange={onContextTextChange}
            />

        </div>
    );
};

export default TripEditInfo;
